import React, {useCallback, useEffect, useRef} from 'react';
import {usePanorama} from '@wix/yoshi-flow-editor';
import {FormViewer} from '@wix/form-viewer/widget';
import {Accordion, AccordionTheme, TextButton, Divider, TextButtonPriority} from '@wix/wix-ui-tpa/cssVars';
import {FormModel} from '../../../../../../domain/models/checkout/Cart/Form.model';
import {classes} from './AdditionalDetailsStepOpen.st.css';
import {useICUI18n} from '../../../../Hooks/useICUI18n';
import {useFormInstance} from '../../../../Form/useFormInstance';
import {useAdditionalDetailsData} from './WithAdditionalDetailsData';
import {useLocaleKeys} from '../../../../../../locale-keys/LocaleKeys';

export enum AdditionalDetailsFormDataHook {
  formItem = 'AdditionalDetailsForm.formItem',
  formItemAccordion = 'AdditionalDetailsForm.formItemAccordion',
  formItemContent = 'AdditionalDetailsForm.formItemContent',
  nextButton = 'AdditionalDetailsForm.nextButton',
}

interface AdditionalDetailsFormProps {
  form: FormModel;
  isInitiallyExpanded: boolean;
  shouldShowNextButton: boolean;
}

export const AdditionalDetailsForm: React.FC<AdditionalDetailsFormProps> = ({
  form,
  isInitiallyExpanded,
  shouldShowNextButton,
}) => {
  const {id, formSchemaId, title, description} = form;
  const i18n = useICUI18n();
  const panorama = usePanorama();

  const localeKeys = useLocaleKeys();
  const {data: formInstance} = useFormInstance({});
  const {registerForm, unregisterForm, isFormValid, submitAndContinueToNextForm} = useAdditionalDetailsData();

  const {formValues, formErrors, formRef, setFormValues, setFormErrors} = formInstance;
  const accordionRef = useRef<Accordion>(null);

  const focusOnForm = useCallback(
    () => {
      accordionRef.current?.expandItem(id);

      setTimeout(() => {
        accordionRef.current?.rootRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 300);
    },
    /* eslint-disable react-hooks/exhaustive-deps */ []
  );

  const handleNextButtonClick = async () => {
    const isValid = await isFormValid(id);
    if (!isValid) {
      return;
    }

    submitAndContinueToNextForm({currentForId: id});
    accordionRef.current?.collapseItem(id);
  };

  useEffect(
    () => {
      registerForm(id, {
        focusOnForm,
        formInstance,
      });

      return () => {
        unregisterForm(id);
      };
    },
    /* eslint-disable react-hooks/exhaustive-deps*/ [id, formRef]
  );

  return (
    <li className={classes.eventItem} data-hook={AdditionalDetailsFormDataHook.formItem}>
      <Accordion
        ref={accordionRef}
        data-hook={AdditionalDetailsFormDataHook.formItemAccordion}
        theme={AccordionTheme.Box}
        initiallyExpanded={isInitiallyExpanded ? [id] : undefined}>
        <Accordion.Item id={id} key={id} title={title} subtitle={description?.join(' | ')}>
          <div
            role="presentation"
            onKeyDown={(e) => e.stopPropagation()}
            className={classes.formItemContent}
            data-hook={AdditionalDetailsFormDataHook.formItemContent}>
            <Divider className={classes.divider} />
            <FormViewer
              i18n={i18n}
              ref={formRef}
              panorama={panorama}
              formId={formSchemaId}
              values={formValues}
              errors={formErrors}
              onChange={setFormValues}
              onValidate={setFormErrors}
            />

            {shouldShowNextButton && (
              <TextButton
                data-hook={AdditionalDetailsFormDataHook.nextButton}
                priority={TextButtonPriority.link}
                className={classes.nextButton}
                onClick={() => void handleNextButtonClick()}>
                {localeKeys.checkout.additionalDetailsStep.next.cta()}
              </TextButton>
            )}
          </div>
        </Accordion.Item>
      </Accordion>
    </li>
  );
};
